import { forwardRef, type HTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { cn } from "../../lib/utils";

export const Card = ({ className, ...p }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("rounded-[14px] border border-border bg-surface shadow-card", className)} {...p} />
);

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...p }, ref) => (
  <input
    ref={ref}
    className={cn(
      "h-8 w-full rounded-lg border border-border-strong bg-surface px-2.5 text-sm outline-none placeholder:text-subtle",
      "focus:border-accent focus:ring-2 focus:ring-accent/20",
      className,
    )}
    {...p}
  />
));
Input.displayName = "Input";

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, ...p }, ref) => (
  <select
    ref={ref}
    className={cn(
      "h-8 rounded-lg border border-border-strong bg-surface px-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20",
      className,
    )}
    {...p}
  />
));
Select.displayName = "Select";

type Tone = "neutral" | "good" | "info" | "warn" | "accent";
const tones: Record<Tone, string> = {
  neutral: "bg-surface-2 text-muted",
  good: "bg-good/10 text-good",
  info: "bg-info-bg text-info-fg",
  warn: "bg-warn-bg text-warn-fg",
  accent: "bg-accent-soft text-accent",
};
export const Badge = ({ tone = "neutral", className, ...p }: HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) => (
  <span
    className={cn("inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4", tones[tone], className)}
    {...p}
  />
);

export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="rounded border border-border-strong bg-surface-2 px-1 font-sans text-[10px] text-subtle">{children}</kbd>
);

export function Tip({ content, children, side = "top" }: { content?: ReactNode; children: ReactNode; side?: "top" | "right" | "bottom" | "left" }) {
  if (!content) return <>{children}</>;
  return (
    <TooltipPrimitive.Root delayDuration={250}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={6}
          className="z-[100] max-w-72 rounded-lg bg-primary px-2.5 py-1.5 text-xs leading-snug text-primary-foreground shadow-pop"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
export const TooltipProvider = TooltipPrimitive.Provider;

export function Switch({ checked, onCheckedChange, id }: { checked: boolean; onCheckedChange: (v: boolean) => void; id?: string }) {
  return (
    <SwitchPrimitive.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      className="relative h-[18px] w-8 shrink-0 rounded-full bg-border-strong transition-colors data-[state=checked]:bg-accent outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
    >
      <SwitchPrimitive.Thumb className="block size-3.5 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[16px]" />
    </SwitchPrimitive.Root>
  );
}

export const Label = ({ className, ...p }: HTMLAttributes<HTMLLabelElement> & { htmlFor?: string }) => (
  <label className={cn("text-[11px] font-medium uppercase tracking-wide text-subtle", className)} {...p} />
);

export const Skeleton = ({ className }: { className?: string }) => <div className={cn("skeleton", className)} />;
